import { request } from "@prismicio/editor-support/Http"
import { useDebounce } from "@prismicio/editor-support/React"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import { useRef } from "react"
import { array, number, object, string, type TypeOf } from "zod"

import { useEditorConfig } from "../../EditorConfig"
import { getSearchTileImage } from "../imageUtils"

const unsplashImage = object({
  id: string(),
  width: number(),
  height: number(),
  urls: object({
    full: string(),
    small: string().transform(getSearchTileImage),
  }),
  alt_description: string().optional(),
  description: string().nullable(),
})

export const unsplashSearchApiType = object({
  results: array(unsplashImage),
}).transform((res) => res.results)

export type UnplashSearchApi = TypeOf<typeof unsplashSearchApiType>
export type UnsplashImage = TypeOf<typeof unsplashImage>

function searchUnsplash(baseUrl: URL, term: string): Promise<UnplashSearchApi> {
  const requestUrl = new URL("search/photos", baseUrl)
  requestUrl.searchParams.set("page", "1")
  requestUrl.searchParams.set("per_page", "28")
  requestUrl.searchParams.set("query", term)

  return request(requestUrl, { okType: unsplashSearchApiType })
}

// cacheCapacity: We need some caching for suspense with the debounce to work well,
//   probably at least 3 but the default of 100 would be very overkill.
// revalidationStrategy: This is non critical data, no need to ever revalidate it.
cache.registerFetcherConfig(searchUnsplash, {
  cacheCapacity: 5,
  revalidationStrategy: { type: "never" },
})

export function useUnsplashSearch(baseUrl: URL, term: string) {
  // The hook will be triggered even when w.g. clicking on an image or with the default
  // search phrase, producing spurious analytic events -  we track the search term
  // changes to avoid that and start with empty calue to skip the default term
  const lastTerm = useRef<string>()
  const editorConfig = useEditorConfig()

  const result = useRequest(searchUnsplash, [baseUrl, term])

  useDebounce(term, 1000, () => {
    if (lastTerm.current && term !== lastTerm.current)
      editorConfig.onAnalyticsEvent?.("Unsplash Image Searched")

    lastTerm.current = term
  })

  return result
}
